type OrchidDriveDownloadProgressReducerResetAction = {
  type: "reset";
};

type DownloadSource =
  | {
      operation: "view" | "download";
      fileDataId: string;
    }
  | {
      operation: "batch";
      batchDownloadName: string;
    };

type OrchidDriveDownloadProgressReducerStatusAction = {
  type: "status";
  timeStamp: Date;
  oldTimeStampThreshold: number;
  downloadSource: DownloadSource;
  status: "gettingMetaData" | "inProgress" | "done";
  totalChunkCount?: number;
  files?: {
    [name: string]: {
      fileTotalChunkCount: number;
    };
  };
};

type OrchidDriveDownloadProgressReducerFailedAction = {
  type: "failed";
  timeStamp: Date;
  downloadSource: DownloadSource;
  error: string;
};

type OrchidDriveDownloadProgressReducerProgressAction = {
  type: "progress";
  timeStamp: Date;
  downloadSource: DownloadSource;
  fileDataId: string;
  chunksDownloaded: number;
};

function filterOutOldCompletedDownloads(
  state: OrchidDriveDownloadProgress[],
  oldTimeStampThreshold: number,
): OrchidDriveDownloadProgress[] {
  function entryOld(entry: OrchidDriveDownloadProgress) {
    return entry.lastUpdateTimeStamp.getTime() < oldTimeStampThreshold;
  }
  if (!state.some(entryOld)) return state;
  return state.filter(entryOld);
}

type SingleDownloadProgress = {
  operation: "view" | "download";
  status: "gettingMetaData" | "inProgress" | "done" | "failed";
  startTimeStamp: Date;
  lastUpdateTimeStamp: Date;
  fileDataId: string;
  error?: string;
  totalChunkCount?: number;
  chunksDownloaded: number;
  downloadPercent?: number;
};

type BatchFileProgress = {
  [name: string]: {
    fileTotalChunkCount?: number;
    chunksDownloaded: number;
    downloadPercent?: number;
  };
};

type BatchDownloadProgress = {
  operation: "batch";
  status: "gettingMetaData" | "inProgress" | "done" | "failed";
  startTimeStamp: Date;
  lastUpdateTimeStamp: Date;
  batchDownloadName: string;
  error?: string;
  totalChunkCount?: number;
  chunksDownloaded: number;
  downloadPercent?: number;
  files: BatchFileProgress;
};

export type OrchidDriveDownloadProgress =
  | SingleDownloadProgress
  | BatchDownloadProgress;

function getMatchingDownloadProgress(
  state: OrchidDriveDownloadProgress[],
  downloadSource: DownloadSource,
): undefined | OrchidDriveDownloadProgress {
  return state.find(
    (e) =>
      e.operation === downloadSource.operation &&
      (("batchDownloadName" in downloadSource &&
        e["batchDownloadName"] === downloadSource["batchDownloadName"]) ||
        ("fileDataId" in downloadSource &&
          e["fileDataId"] === downloadSource["fileDataId"])),
  );
}

function updateStateSingleDownloadStatus(
  progress: SingleDownloadProgress,
  timeStamp: Date,
  status: "gettingMetaData" | "inProgress" | "done",
  totalChunkCount?: number,
): SingleDownloadProgress {
  return {
    ...progress,
    lastUpdateTimeStamp: timeStamp,
    status,
    totalChunkCount: totalChunkCount || progress.totalChunkCount,
  };
}

function updateStateBatchDownloadStatus(
  progress: BatchDownloadProgress,
  timeStamp: Date,
  status: "gettingMetaData" | "inProgress" | "done",
  totalChunkCount?: number,
  files?: {
    [name: string]: {
      fileTotalChunkCount: number;
    };
  },
): BatchDownloadProgress {
  const fileProgress: BatchFileProgress = {};
  for (const [fileDataId, file] of Object.entries(files || {})) {
    fileProgress[fileDataId] = {
      fileTotalChunkCount: file.fileTotalChunkCount,
      chunksDownloaded: 0,
      downloadPercent: file.fileTotalChunkCount ? 0 : undefined,
    };
  }

  return {
    ...progress,
    lastUpdateTimeStamp: timeStamp,
    status,
    totalChunkCount: totalChunkCount || progress.totalChunkCount,
    files: files ? fileProgress : progress.files,
  };
}

function updateStateDownloadFailed(
  progress: OrchidDriveDownloadProgress,
  timeStamp: Date,
  error: string,
): OrchidDriveDownloadProgress {
  return {
    ...progress,
    lastUpdateTimeStamp: timeStamp,
    status: "failed",
    error,
  };
}

function updateStateSingleDownloadProgress(
  progress: SingleDownloadProgress,
  timeStamp: Date,
  chunksDownloaded: number,
): SingleDownloadProgress {
  return {
    ...progress,
    lastUpdateTimeStamp: timeStamp,
    status: "inProgress",
    chunksDownloaded,
    downloadPercent: progress.totalChunkCount
      ? Math.min(100, (chunksDownloaded * 100) / progress.totalChunkCount)
      : undefined,
  };
}

function updateStateBatchDownloadProgress(
  progress: BatchDownloadProgress,
  timeStamp: Date,
  fileDataId: string,
  chunksDownloaded: number,
): BatchDownloadProgress {
  const existingFileEntry = progress.files[fileDataId];
  const newFileEntry = existingFileEntry
    ? {
        ...existingFileEntry,
        chunksDownloaded,
        downloadPercent: existingFileEntry.fileTotalChunkCount
          ? Math.min(
              100,
              (chunksDownloaded * 100) / existingFileEntry.fileTotalChunkCount,
            )
          : undefined,
      }
    : {
        chunksDownloaded,
      };

  const newFiles = {
    ...progress.files,
    [fileDataId]: newFileEntry,
  };

  const totalChunksDownloaded = Object.values(newFiles).reduce(
    (accu, e) => accu + e.chunksDownloaded,
    0,
  );

  return {
    ...progress,
    lastUpdateTimeStamp: timeStamp,
    status: "inProgress",
    chunksDownloaded: totalChunksDownloaded,
    downloadPercent: progress.totalChunkCount
      ? Math.min(100, (chunksDownloaded * 100) / progress.totalChunkCount)
      : undefined,
    files: newFiles,
  };
}

export default function OrchidDriveDownloadProgressReducer(
  state: OrchidDriveDownloadProgress[],
  action:
    | OrchidDriveDownloadProgressReducerResetAction
    | OrchidDriveDownloadProgressReducerStatusAction
    | OrchidDriveDownloadProgressReducerFailedAction
    | OrchidDriveDownloadProgressReducerProgressAction,
) {
  switch (action.type) {
    case "reset": {
      if (state.length === 0) return state;
      return [];
    }
    case "status": {
      const downloadProgress =
        getMatchingDownloadProgress(state, action.downloadSource) ||
        (action.downloadSource.operation === "batch"
          ? {
              operation: "batch",
              status: action.status,
              startTimeStamp: action.timeStamp,
              lastUpdateTimeStamp: action.timeStamp,
              batchDownloadName: action.downloadSource.batchDownloadName,
              error: undefined,
              totalChunkCount: action.totalChunkCount,
              chunksDownloaded: 0,
              downloadPercent: undefined,
              files: {},
            }
          : {
              operation: action.downloadSource.operation,
              status: action.status,
              startTimeStamp: action.timeStamp,
              lastUpdateTimeStamp: action.timeStamp,
              fileDataId: action.downloadSource.fileDataId,
              error: undefined,
              totalChunkCount: action.totalChunkCount,
              chunksDownloaded: 0,
              downloadPercent: undefined,
            });
      if (downloadProgress.status === "failed") return state;

      const newState: OrchidDriveDownloadProgress[] = [
        ...state.filter((e) => e !== downloadProgress),
        downloadProgress.operation === "batch"
          ? updateStateBatchDownloadStatus(
              downloadProgress,
              action.timeStamp,
              action.status,
              action.totalChunkCount,
              action.files,
            )
          : updateStateSingleDownloadStatus(
              downloadProgress,
              action.timeStamp,
              action.status,
              action.totalChunkCount,
            ),
      ];

      return filterOutOldCompletedDownloads(
        newState,
        action.oldTimeStampThreshold,
      );
    }
    case "failed": {
      const downloadProgress = getMatchingDownloadProgress(
        state,
        action.downloadSource,
      );
      if (!downloadProgress) return state;

      return [
        ...state.filter((e) => e !== downloadProgress),
        updateStateDownloadFailed(
          downloadProgress,
          action.timeStamp,
          action.error,
        ),
      ];
    }
    case "progress": {
      const downloadProgress = getMatchingDownloadProgress(
        state,
        action.downloadSource,
      );
      if (!downloadProgress || downloadProgress.status === "failed")
        return state;

      // sanity check
      if (
        action.downloadSource.operation !== "batch" &&
        action.downloadSource.fileDataId !== downloadProgress["fileDataId"]
      ) {
        throw new Error(
          "unexpected progress on unrelated fileDataId, this should never happen",
        );
      }

      return [
        ...state.filter((e) => e !== downloadProgress),
        downloadProgress.operation === "batch"
          ? updateStateBatchDownloadProgress(
              downloadProgress,
              action.timeStamp,
              action.fileDataId,
              action.chunksDownloaded,
            )
          : updateStateSingleDownloadProgress(
              downloadProgress,
              action.timeStamp,
              action.chunksDownloaded,
            ),
      ];
    }
  }
  throw new Error(`unexpected action type: ${action}`);
}
